/**
 * Closed-loan-package review section (acquisitions only).
 *
 * When we BUY a closed loan, the diligence isn't "did we underwrite it right" —
 * it's "did the correspondent close it right, and do we have the paper." These
 * are the `category: 'closed_package'` fields on LoanAcquisitionBaseModel:
 *   noteReviewStatus / noteReviewedBy / noteReviewDate  — the note review
 *   termsValidated                                      — extracted note terms
 *                                                         reconcile to the tape
 *   collateralReceived / collateralTrackingNumber       — the physical file
 *   assignmentChainComplete / allongeReceived           — chain of assignment
 *   titlePolicyReceived / recordedMortgageReceived      — recorded paper
 *
 * All save through POST update_loan_data/{dealId}, which useUnderwritingHook
 * already re-points to /loan_acquisitions via PipelineContext.
 *
 * Renders as a `LoanSectionCard` — a peer section under the tab's page header,
 * per the Drilldown Tab Header Standard.
 */
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import { TabGhostButton } from "../../shared/tabLayout/tabButtons";
import { TAB_TOKENS as T, orDash } from "../../shared/tabLayout/tabTokens";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";

// Mirrors note_review_status_options in app/models/loan_acquisitions.py.
const NOTE_REVIEW_STATUSES = [
  "Not Reviewed",
  "In Review",
  "Passed",
  "Exception",
  "Failed",
];

const NOTE_REVIEW_STYLE = {
  Passed: { background: T.color.okSoft, color: T.color.okInk },
  "In Review": { background: T.color.brandSoft, color: T.color.brand },
  Exception: { background: T.color.warnSoft, color: T.color.warnInk },
  Failed: { background: T.color.dangerSoft, color: T.color.dangerInk },
  "Not Reviewed": { background: "#f1f5f9", color: T.color.ink2 },
};

/**
 * The boolean checkpoints, in the order the file actually moves. Exported so the
 * tab's KPI strip counts the same list the card renders — one source, so the
 * tile and the section can't disagree.
 */
export const CLOSED_PACKAGE_CHECKS = [
  {
    key: "termsValidated",
    label: "Note terms validated",
    help: "Extracted note terms reconcile to the trade tape",
  },
  {
    key: "collateralReceived",
    label: "Collateral received",
    help: "Original note / collateral file in hand",
  },
  {
    key: "allongeReceived",
    label: "Allonge received",
  },
  {
    key: "assignmentChainComplete",
    label: "Assignment chain complete",
    help: "Chain of assignment / allonge is unbroken",
  },
  {
    key: "titlePolicyReceived",
    label: "Title policy received",
  },
  {
    key: "recordedMortgageReceived",
    label: "Recorded mortgage received",
  },
];

/** Read-only label/value pair, per the standard's field type scale. */
const Field = ({ label, value }) => (
  <Flx column sx={{ gap: "3px" }}>
    <Txt sx={T.type.fieldLabel}>{label}</Txt>
    <Txt sx={T.type.fieldValue}>{value}</Txt>
  </Flx>
);

const ClosedPackageReviewBlock = () => {
  const dispatch = useDispatch();
  const { updateLoanData } = useUnderwritingHook();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;

  const [saving, setSaving] = useState(null); // the field key being saved
  const [tracking, setTracking] = useState(null); // local edit buffer

  const save = useCallback(
    async (data, key) => {
      if (!dealId) return;
      setSaving(key);
      await updateLoanData({
        loanId: dealId,
        data,
        onSuccessMsg: "Closed package updated",
        onSuccessFn: (updated) => {
          if (updated?._id) dispatch(loanDrilldownSet(updated));
        },
        onCompleteFn: () => setSaving(null),
      });
    },
    [dealId, updateLoanData, dispatch],
  );

  const completed = useMemo(
    () => CLOSED_PACKAGE_CHECKS.filter((c) => loan?.[c.key] === true).length,
    [loan],
  );

  const noteStatus = loan?.noteReviewStatus || "Not Reviewed";

  return (
    <LoanSectionCard
      icon={<FactCheckOutlined />}
      title="Closed package review"
      subTitle="The paper and terms behind a purchased loan: note review, collateral, chain of assignment, and recorded documents."
      count={`${completed} of ${CLOSED_PACKAGE_CHECKS.length} complete`}
      controls={
        <Chip
          size="small"
          label={`Note: ${noteStatus}`}
          sx={{
            height: 21,
            fontSize: 11,
            fontWeight: 600,
            ...(NOTE_REVIEW_STYLE[noteStatus] || {}),
          }}
        />
      }
      disableBodyPadding
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "360px 1fr",
          "@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
        }}
      >
        {/* ── Left: note review + collateral ── */}
        <Flx
          column
          sx={{
            gap: "16px",
            padding: "18px",
            borderRight: `1px solid ${T.color.border}`,
            "@media (max-width: 960px)": {
              borderRight: "none",
              borderBottom: `1px solid ${T.color.border}`,
            },
          }}
        >
          <TextField
            select
            size="small"
            label="Note review status"
            value={noteStatus}
            disabled={saving === "noteReviewStatus"}
            onChange={(e) =>
              save({ noteReviewStatus: e.target.value }, "noteReviewStatus")
            }
            InputProps={{ sx: { fontSize: 13 } }}
            InputLabelProps={{ sx: { fontSize: 13 } }}
          >
            {NOTE_REVIEW_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          {/* Reviewer + date are stamped by whoever clears the note; show them
              read-only rather than letting the UI invent an author. */}
          <Flx sx={{ gap: "24px" }}>
            <Field label="Reviewed by" value={orDash(loan?.noteReviewedBy)} />
            <Field
              label="Review date"
              value={formatDate(loan?.noteReviewDate, "—")}
            />
          </Flx>

          <Flx column sx={{ gap: "6px" }}>
            <Txt sx={T.type.fieldLabel}>Collateral tracking #</Txt>
            <Flx ac sx={{ gap: "8px" }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Shipping / tracking number"
                value={tracking ?? loan?.collateralTrackingNumber ?? ""}
                onChange={(e) => setTracking(e.target.value)}
                InputProps={{ sx: { fontSize: 13 } }}
              />
              <TabGhostButton
                size="sm"
                label="Save"
                disabled={
                  tracking === null ||
                  tracking === (loan?.collateralTrackingNumber ?? "") ||
                  saving === "collateralTrackingNumber"
                }
                onClick={() =>
                  save(
                    { collateralTrackingNumber: tracking },
                    "collateralTrackingNumber",
                  ).then(() => setTracking(null))
                }
              />
            </Flx>
          </Flx>
        </Flx>

        {/* ── Right: checkpoints, in the order the file moves ── */}
        <Box sx={{ padding: "6px 18px 8px" }}>
          {CLOSED_PACKAGE_CHECKS.map((c) => {
            const done = loan?.[c.key] === true;
            return (
              <Flx
                key={c.key}
                ac
                sx={{
                  gap: "13px",
                  paddingBlock: "11px",
                  borderBottom: `1px solid ${T.color.border}`,
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                {/* Read-only completion mark — reflects state; the switch is the
                    control. */}
                <Flx
                  center
                  sx={{
                    flexShrink: 0,
                    width: 22,
                    height: 22,
                    borderRadius: "99px",
                    border: `1.5px solid ${done ? T.color.okInk : T.color.borderStrong}`,
                    background: done ? T.color.okInk : "transparent",
                    color: "#fff",
                    transition: "all .15s",
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                    "& svg": { fontSize: 14 },
                  }}
                >
                  {done && <CheckRounded />}
                </Flx>
                <Flx column sx={{ gap: "1px", minWidth: 0, flex: 1 }}>
                  <Txt
                    sx={{
                      ...T.type.fieldValue,
                      color: done ? T.color.ink2 : T.color.ink,
                    }}
                  >
                    {c.label}
                  </Txt>
                  {c.help && <Txt sx={T.type.meta}>{c.help}</Txt>}
                </Flx>
                <Switch
                  size="small"
                  checked={done}
                  disabled={saving === c.key}
                  onChange={(e) => save({ [c.key]: e.target.checked }, c.key)}
                />
              </Flx>
            );
          })}
        </Box>
      </Box>
    </LoanSectionCard>
  );
};

export default ClosedPackageReviewBlock;
